import { useMemo, useState } from 'react';
import { CalendarDays, Check, Link2, Pencil, Plus, Save, Trash2, Video } from 'lucide-react';

const EMPTY_FORM = {
  title: '',
  description: '',
  day: '',
  live_start_at: '',
  live_end_at: '',
  join_url: '',
  recording_url: '',
  replay_access_hours: 48,
  is_active: true,
};

const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;

function toInputDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  // Convert UTC → IST explicitly (browser timezone ignored)
  return new Date(d.getTime() + IST_OFFSET_MS).toISOString().slice(0, 16);
}

function istLocalToUtcIso(dtLocalStr) {
  if (!dtLocalStr) return null;
  // Treat datetime-local value as IST by parsing as UTC then subtracting the IST offset
  return new Date(new Date(dtLocalStr + 'Z').getTime() - IST_OFFSET_MS).toISOString();
}

function toPayload(form) {
  return {
    title: form.title.trim(),
    description: form.description?.trim() || null,
    day: form.day ? Number(form.day) : null,
    live_start_at: istLocalToUtcIso(form.live_start_at),
    live_end_at: istLocalToUtcIso(form.live_end_at),
    join_url: form.join_url?.trim() || null,
    recording_url: form.recording_url?.trim() || null,
    replay_access_hours: form.replay_access_hours ? Number(form.replay_access_hours) : 48,
    is_active: !!form.is_active,
  };
}

export default function AdminSessionManager({
  sessions = [],
  loading = false,
  saving = false,
  onCreateSession,
  onUpdateSession,
  onDeleteSession,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);

  const sortedSessions = useMemo(
    () => [...sessions].sort((a, b) => new Date(a.live_start_at) - new Date(b.live_start_at)),
    [sessions]
  );

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const startEdit = (session) => {
    setEditingId(session._id || session.id);
    setForm({
      title: session.title || '',
      description: session.description || '',
      day: session.day ?? '',
      live_start_at: toInputDateTime(session.live_start_at),
      live_end_at: toInputDateTime(session.live_end_at),
      join_url: session.join_url || '',
      recording_url: session.recording_url || '',
      replay_access_hours: session.replay_access_hours ?? 48,
      is_active: session.is_active ?? true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = toPayload(form);
    if (!payload.title || !payload.live_start_at || !payload.live_end_at) return;

    if (editingId) {
      await onUpdateSession?.(editingId, payload);
    } else {
      await onCreateSession?.(payload);
    }
    resetForm();
  };

  const isEditing = Boolean(editingId);

  return (
    <section className="bg-white border border-[#E7E5E4] rounded-2xl overflow-hidden">
      <div className="px-5 py-4 border-b border-[#E7E5E4] bg-[#F9F7F3] flex items-center justify-between">
        <div>
          <h3 className="font-heading text-xl font-bold text-[#292524]">Live Session Management</h3>
          <p className="text-sm text-[#78716C]">Create and edit live class sessions, Zoom links, and replay windows.</p>
        </div>
        {isEditing && (
          <button type="button" onClick={resetForm} className="text-sm text-[#9A3B26] font-medium hover:underline">
            Cancel Edit
          </button>
        )}
      </div>

      <div className="p-4 sm:p-6 grid xl:grid-cols-[1.2fr_1fr] gap-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Session Title" required>
              <input
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                className="w-full rounded-xl border border-[#D6D3D1] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#9A3B26]/30"
                placeholder="Day 7 - Warrior Flow"
                required
              />
            </Field>

            <Field label="Day Number">
              <input
                type="number"
                min="0"
                max="21"
                value={form.day}
                onChange={(e) => setForm((p) => ({ ...p, day: e.target.value }))}
                className="w-full rounded-xl border border-[#D6D3D1] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#9A3B26]/30"
                placeholder="7"
              />
            </Field>
          </div>

          <Field label="Description">
            <textarea
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              className="w-full rounded-xl border border-[#D6D3D1] px-3 py-2.5 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-[#9A3B26]/30"
              placeholder="Focus: lower body strength, breath rhythm, and posture refinement."
            />
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Live Start (IST)" required icon={<CalendarDays className="h-4 w-4" />}>
              <input
                type="datetime-local"
                value={form.live_start_at}
                onChange={(e) => setForm((p) => ({ ...p, live_start_at: e.target.value }))}
                className="w-full rounded-xl border border-[#D6D3D1] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#9A3B26]/30"
                required
              />
            </Field>

            <Field label="Live End (IST)" required icon={<CalendarDays className="h-4 w-4" />}>
              <input
                type="datetime-local"
                value={form.live_end_at}
                onChange={(e) => setForm((p) => ({ ...p, live_end_at: e.target.value }))}
                className="w-full rounded-xl border border-[#D6D3D1] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#9A3B26]/30"
                required
              />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Zoom / Join URL" icon={<Video className="h-4 w-4" />}>
              <input
                type="url"
                value={form.join_url}
                onChange={(e) => setForm((p) => ({ ...p, join_url: e.target.value }))}
                className="w-full rounded-xl border border-[#D6D3D1] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#9A3B26]/30"
                placeholder="https://zoom.us/j/..."
              />
            </Field>

            <Field label="Recording / Replay URL" icon={<Link2 className="h-4 w-4" />}>
              <input
                type="url"
                value={form.recording_url}
                onChange={(e) => setForm((p) => ({ ...p, recording_url: e.target.value }))}
                className="w-full rounded-xl border border-[#D6D3D1] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#9A3B26]/30"
                placeholder="https://..."
              />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 items-end">
            <Field label="Replay Access (hours)">
              <input
                type="number"
                min="1"
                max="720"
                value={form.replay_access_hours}
                onChange={(e) => setForm((p) => ({ ...p, replay_access_hours: e.target.value }))}
                className="w-full rounded-xl border border-[#D6D3D1] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#9A3B26]/30"
                placeholder="48"
              />
            </Field>

            <label className="inline-flex items-center gap-2 text-sm text-[#57534E] pb-2">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                className="h-4 w-4 rounded border-[#D6D3D1] text-[#9A3B26] focus:ring-[#9A3B26]/30"
              />
              Session active
            </label>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl bg-[#9A3B26] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#7A2E1E] disabled:opacity-50"
          >
            {isEditing ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {saving ? 'Saving...' : isEditing ? 'Update Session' : 'Create Session'}
          </button>
        </form>

        <div className="border border-[#E7E5E4] rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-[#F9F7F3] border-b border-[#E7E5E4]">
            <h4 className="font-semibold text-[#292524]">Existing Sessions ({sortedSessions.length})</h4>
          </div>

          <div className="max-h-[520px] overflow-auto divide-y divide-[#E7E5E4]">
            {loading ? (
              <p className="p-4 text-sm text-[#78716C]">Loading sessions...</p>
            ) : sortedSessions.length === 0 ? (
              <p className="p-4 text-sm text-[#78716C]">No sessions created yet.</p>
            ) : (
              sortedSessions.map((session) => {
                const id = session._id || session.id;
                return (
                  <div key={id} className="p-4">
                    <p className="font-medium text-[#292524]">{session.title}</p>
                    <p className="text-xs text-[#78716C] mt-1">
                      Day {session.day ?? '-'} &bull;{' '}
                      {session.live_start_at ? new Date(session.live_start_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) + ' IST' : 'TBD'}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {session.join_url && (
                        <span className="inline-flex items-center gap-1 text-xs bg-[#EEF6FF] text-[#1D4ED8] px-2 py-1 rounded-full">
                          <Video className="h-3.5 w-3.5" /> Zoom
                        </span>
                      )}
                      {session.recording_url && (
                        <span className="inline-flex items-center gap-1 text-xs bg-[#F4EFE6] text-[#9A3B26] px-2 py-1 rounded-full">
                          <Link2 className="h-3.5 w-3.5" /> Replay
                        </span>
                      )}
                      {session.is_active && (
                        <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full">
                          <Check className="h-3.5 w-3.5" /> Active
                        </span>
                      )}
                    </div>
                    <div className="mt-3 flex items-center gap-4">
                      <button
                        type="button"
                        onClick={() => startEdit(session)}
                        className="inline-flex items-center gap-1.5 text-sm text-[#9A3B26] font-medium hover:underline"
                      >
                        <Pencil className="h-4 w-4" /> Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => onDeleteSession?.(id)}
                        className="inline-flex items-center gap-1.5 text-sm text-red-500 font-medium hover:underline"
                      >
                        <Trash2 className="h-4 w-4" /> Delete
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required = false, icon = null, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 text-sm font-medium text-[#44403C] inline-flex items-center gap-1.5">
        {icon}
        {label}
        {required ? <span className="text-[#9A3B26]">*</span> : null}
      </span>
      {children}
    </label>
  );
}
